import { CORE_LOADER_TYPE } from '@/components/core/Loader/Loader';
import Feed, { FEED_THEMES } from '@/components/features/feed/Feed';
import { createClient } from '@/jsonapi/createClient';
import { FEED_ACTION_TYPES } from '@/utils/constants';
import { getSafeLocale } from '@/utils/locale';
import { serializeCollectionResource, serializeSingleResource } from '@/utils/serilize-resource';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import useSWRInfinite from 'swr/infinite';
import { useClient } from '@/components/shared/utilities/AppProvider/AppProvider';
import { useCallback, useMemo } from 'react';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { prependUnique } from '@/utils/prependUnique';
import { FeedLayout } from '@/components/shared/layouts/FeedLayout/FeedLayout';
import { useRouter } from 'next/router';

type ExploreProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const ExplorePage: NextPage<ExploreProps> = ({ postData, postsData }) => {
	const { t } = useTranslation();
	const { query } = useRouter();
	const client = useClient();

	const {
		data: posts,
		error,
		setSize,
		size,
	} = useSWRInfinite(
		(pageIndex, previousPageData) => {
			if (previousPageData && !previousPageData?.links?.next) return null; // reached the end

			const pageNumber = pageIndex + 1;

			return [
				'posts',
				{
					include: 'favoriter',
					page: pageNumber,
					per_page: 3,
					filters: query.filter === 'trending' ? undefined : query.filter,
				},
			] as const;
		},
		async (key) => {
			const data = await client.findAll(...key);

			return serializeCollectionResource(data);
		},
		{
			fallbackData: [postsData],
		}
	);

	const { items, isLoadingMore, isReachingEnd } = useMemo(
		() => extractSWRInfiniteData(posts, error, size),
		[posts, error, size]
	);

	const itemsFiltered = useMemo(() => prependUnique(postData?.data, items), [items, postData]);

	const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

	return (
		<FeedLayout>
			<Feed
				headerActionType={FEED_ACTION_TYPES.SHARE_STORY}
				onCloseTo="/"
				headerTitle={t('everything')}
				headerSubtitle={t('theres-always-more-beneath-th')}
				theme={FEED_THEMES.HOME}
				loaderType={CORE_LOADER_TYPE.PRIMARY}
				items={itemsFiltered}
				isReachingEnd={isReachingEnd}
				isLoadingMore={isLoadingMore}
				onLoadMore={onLoadMore}
			/>
		</FeedLayout>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query, req }) => {
	const client = createClient({ locale, cookie: req.headers.cookie });

	const postDataResponse = await client.find('posts', query.id, { include: 'favoriter' });
	const postData = serializeSingleResource(postDataResponse);

	const postsResponse = await client.findAll('posts', {
		include: 'favoriter',
		page: 1,
		per_page: 3,
		filters: query.filter === 'trending' ? undefined : query.filter,
	});
	const postsData = serializeCollectionResource(postsResponse);

	return {
		props: {
			postData,
			postsData,
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default ExplorePage;
